import { useEffect } from "react";
import { tick, confirm, swoosh, isMuted } from "../lib/sounds";

/**
 * Site-wide button sound effects. Listens for clicks on real buttons and
 * picks the right gamified blip based on the button's class:
 *   .btn.primary / .accent → confirm arpeggio
 *   .pill-nav / .nav-link  → swoosh (navigation)
 *   anything else          → light tick
 *
 * Form inputs and elements with `data-no-sound` are skipped. Disabled buttons
 * stay silent.
 */
export function useButtonSounds() {
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (isMuted()) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Don't beep on form interactions or escape-hatch elements.
      if (target.closest("input, textarea, select, [data-no-sound]")) return;

      const btn = target.closest(
        "button, [role='button'], a.btn"
      ) as HTMLElement | null;
      if (!btn) return;
      if ((btn as HTMLButtonElement).disabled) return;

      const cls = typeof btn.className === "string" ? btn.className : "";
      if (/\b(primary|accent)\b/.test(cls)) {
        confirm();
      } else if (/\b(pill-nav|nav-link|nav-account)\b/.test(cls)) {
        swoosh();
      } else {
        tick();
      }
    }

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);
}
